import { useMemo, useState } from 'react';
import { Alert, Button, Card, Container, Form, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAgentAssetEvolutionCandidates } from '../../apps/apis/claudeCodeDashboard/hooks';
import type {
  AgentAssetEvolutionAction,
  AgentAssetEvolutionCandidate,
  AgentAssetEvolutionRisk,
} from '../../apps/apis/claudeCodeDashboard/types';
import {
  ASSET_EVOLUTION_ACTION_HELP,
  HELP_TEXT,
  RISK_HELP,
  ROLE_ASSET_KIND_HELP,
  ROLE_ASSET_SCOPE_HELP,
  ROLE_ASSET_STATUS_HELP,
} from '../../components/claude-code/agentTelemetryHelp';
import ClaudeCodeLoginGate from '../../components/claude-code/ClaudeCodeLoginGate';
import { HelpBadge, HelpLabel } from '../../components/claude-code/HelpTooltip';
import { ROUTES } from '../../routes/routes';
import { formatCost, formatCount, formatDate, kindLabel, roleLabel, scopeLabel, statusVariant } from './roleAssetUi';

const WINDOW_OPTIONS = [7, 14, 30, 60, 90];
const LIMIT_OPTIONS = [25, 50, 100, 250];

const ACTION_FILTERS: Array<{ value: '' | AgentAssetEvolutionAction; label: string }> = [
  { value: '', label: 'All actions' },
  { value: 'investigate_failures', label: 'Investigate failures' },
  { value: 'review_instructions', label: 'Review instructions' },
  { value: 'improve_onboarding', label: 'Improve onboarding' },
];

const RISK_FILTERS: Array<{ value: '' | AgentAssetEvolutionRisk; label: string }> = [
  { value: '', label: 'All risks' },
  { value: 'high', label: 'High risk' },
  { value: 'medium', label: 'Medium risk' },
  { value: 'low', label: 'Low risk' },
];

const AgentAssetEvolutionReviewQueuePage = () => (
  <ClaudeCodeLoginGate>
    {(session) => (
      <Container fluid className="py-4">
        <div className="d-flex align-items-end justify-content-between gap-2 mb-3 flex-wrap">
          <div>
            <Link to={ROUTES.agentTelemetry.overview} className="small text-muted">
              Back to Agent Telemetry
            </Link>
            <h2 className="mb-1">
              <HelpLabel description={HELP_TEXT.sharedAssetHealthReview}>Shared Asset Health Review</HelpLabel>
            </h2>
            <div className="text-muted small">
              Signed in as <strong>{session.email}</strong>
              {session.isAdmin ? <span className="ms-2 badge bg-primary">admin</span> : null}
            </div>
          </div>
          <div className="d-flex gap-2">
            <Link to={ROUTES.agentTelemetry.roleAssets} className="btn btn-outline-secondary btn-sm">
              Role asset library
            </Link>
            <Link to={ROUTES.agentTelemetry.roleAssetsByRole} className="btn btn-outline-primary btn-sm">
              View by role
            </Link>
          </div>
        </div>
        {session.isAdmin ? (
          <ReviewQueueBody />
        ) : (
          <Alert variant="warning">Shared asset health review is admin-only.</Alert>
        )}
      </Container>
    )}
  </ClaudeCodeLoginGate>
);

function ReviewQueueBody() {
  const [windowDays, setWindowDays] = useState(30);
  const [limit, setLimit] = useState(50);
  const [action, setAction] = useState<'' | AgentAssetEvolutionAction>('');
  const [risk, setRisk] = useState<'' | AgentAssetEvolutionRisk>('');
  const query = useAgentAssetEvolutionCandidates(windowDays, limit);
  const candidates = query.data?.candidates ?? [];
  const filteredCandidates = useMemo(
    () =>
      candidates.filter((candidate) => {
        if (action && candidate.recommendedAction !== action) {
          return false;
        }
        if (risk && candidate.riskLevel !== risk) {
          return false;
        }
        return true;
      }),
    [action, candidates, risk],
  );
  const summary = useMemo(
    () => ({
      highRisk: candidates.filter((candidate) => candidate.riskLevel === 'high').length,
      failureReviews: candidates.filter((candidate) => candidate.recommendedAction === 'investigate_failures').length,
      onboardingReviews: candidates.filter((candidate) => candidate.recommendedAction === 'improve_onboarding').length,
    }),
    [candidates],
  );

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex align-items-end justify-content-between gap-3 flex-wrap">
            <div>
              <Card.Title className="h6 mb-1">
                <HelpLabel description={HELP_TEXT.sharedAssetHealthReview}>Shared asset health queue</HelpLabel>
              </Card.Title>
              <Card.Text className="text-muted mb-0">
                Uses metadata-only role asset adoption, usage, failure, and cost signals. No user input or output text
                is shown here.
              </Card.Text>
            </div>
            <div className="d-flex gap-2 flex-wrap align-items-end">
              <Form.Group style={{ minWidth: 130 }}>
                <Form.Label className="small text-muted">
                  <HelpLabel description={HELP_TEXT.reviewQueueWindow}>Window</HelpLabel>
                </Form.Label>
                <Form.Select value={windowDays} onChange={(event) => setWindowDays(Number(event.target.value))}>
                  {WINDOW_OPTIONS.map((days) => (
                    <option key={days} value={days}>
                      {days} days
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group style={{ minWidth: 120 }}>
                <Form.Label className="small text-muted">
                  <HelpLabel description={HELP_TEXT.reviewQueueLimit}>Limit</HelpLabel>
                </Form.Label>
                <Form.Select value={limit} onChange={(event) => setLimit(Number(event.target.value))}>
                  {LIMIT_OPTIONS.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group style={{ minWidth: 190 }}>
                <Form.Label className="small text-muted">
                  <HelpLabel description={HELP_TEXT.reviewQueueAction}>Action</HelpLabel>
                </Form.Label>
                <Form.Select
                  value={action}
                  onChange={(event) => setAction(event.target.value as '' | AgentAssetEvolutionAction)}
                >
                  {ACTION_FILTERS.map((option) => (
                    <option key={option.value || 'all'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group style={{ minWidth: 150 }}>
                <Form.Label className="small text-muted">
                  <HelpLabel description={HELP_TEXT.reviewQueueRisk}>Risk</HelpLabel>
                </Form.Label>
                <Form.Select
                  value={risk}
                  onChange={(event) => setRisk(event.target.value as '' | AgentAssetEvolutionRisk)}
                >
                  {RISK_FILTERS.map((option) => (
                    <option key={option.value || 'all'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Button variant="outline-secondary" onClick={() => void query.refetch()} disabled={query.isFetching}>
                Refresh
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      <div className="d-flex gap-2 flex-wrap mb-3">
        <SummaryBadge
          label="Candidates"
          description="Total candidates returned by the backend before local action/risk filters."
          value={candidates.length}
          variant="secondary"
        />
        <SummaryBadge label="High risk" description={RISK_HELP.high} value={summary.highRisk} variant="danger" />
        <SummaryBadge
          label="Failure reviews"
          description={ASSET_EVOLUTION_ACTION_HELP.investigate_failures}
          value={summary.failureReviews}
          variant="warning"
        />
        <SummaryBadge
          label="Onboarding reviews"
          description={ASSET_EVOLUTION_ACTION_HELP.improve_onboarding}
          value={summary.onboardingReviews}
          variant="info"
        />
      </div>

      {query.isLoading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      ) : null}
      {query.error ? (
        <Alert variant="danger">Failed to load shared asset health candidates: {(query.error as Error).message}</Alert>
      ) : null}
      {!query.isLoading && !query.error ? (
        <CandidateTable candidates={filteredCandidates} generatedAt={query.data?.generatedAt ?? null} />
      ) : null}
    </>
  );
}

function SummaryBadge({
  label,
  description,
  value,
  variant,
}: {
  label: string;
  description: string;
  value: number;
  variant: string;
}) {
  return (
    <HelpBadge bg={variant} className="px-3 py-2" description={description}>
      {label}: {value.toLocaleString()}
    </HelpBadge>
  );
}

function CandidateTable({
  candidates,
  generatedAt,
}: {
  candidates: AgentAssetEvolutionCandidate[];
  generatedAt: string | null;
}) {
  return (
    <Card>
      <Card.Header className="d-flex align-items-center justify-content-between gap-2">
        <div className="fw-semibold">
          <HelpLabel description={HELP_TEXT.reviewCandidates}>Review candidates</HelpLabel>
        </div>
        <div className="text-muted small">
          <HelpLabel description="Backend generation timestamp for this review candidate set.">
            Generated {formatDate(generatedAt)}
          </HelpLabel>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        {candidates.length ? (
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>
                    <HelpLabel description={HELP_TEXT.asset}>Asset</HelpLabel>
                  </th>
                  <th>
                    <HelpLabel description={HELP_TEXT.assetTargeting}>Targeting</HelpLabel>
                  </th>
                  <th>
                    <HelpLabel description={HELP_TEXT.reviewQueueAction}>Action</HelpLabel>
                  </th>
                  <th>
                    <HelpLabel description={HELP_TEXT.reviewQueueRisk}>Risk</HelpLabel>
                  </th>
                  <th>
                    <HelpLabel description={HELP_TEXT.adoption}>Adoption</HelpLabel>
                  </th>
                  <th>
                    <HelpLabel description={HELP_TEXT.usage}>Usage</HelpLabel>
                  </th>
                  <th>
                    <HelpLabel description={HELP_TEXT.failures}>Failures</HelpLabel>
                  </th>
                  <th>
                    <HelpLabel description={HELP_TEXT.costUsd}>Cost</HelpLabel>
                  </th>
                  <th>
                    <HelpLabel description={HELP_TEXT.suggestedNextStep}>Next step</HelpLabel>
                  </th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((candidate) => (
                  <CandidateRow key={candidate.assetId} candidate={candidate} />
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <div className="p-4 text-muted">No shared asset health candidates match these filters.</div>
        )}
      </Card.Body>
    </Card>
  );
}

function CandidateRow({ candidate }: { candidate: AgentAssetEvolutionCandidate }) {
  return (
    <tr>
      <td style={{ minWidth: 300 }}>
        <Link to={ROUTES.agentTelemetry.roleAssetDetail(candidate.assetId)} className="fw-semibold">
          {candidate.title}
        </Link>
        <div className="text-muted small">
          <HelpLabel description={ROLE_ASSET_KIND_HELP[candidate.kind]}>{kindLabel(candidate.kind)}</HelpLabel> ·{' '}
          {candidate.adapter} · {candidate.assetId}
        </div>
        <div className="text-muted small">
          active {candidate.activeVersion ?? '-'} / latest {candidate.latestVersion} · updated{' '}
          {formatDate(candidate.updatedAt)}
        </div>
      </td>
      <td style={{ minWidth: 220 }}>
        <div>
          <HelpLabel description={ROLE_ASSET_SCOPE_HELP[candidate.scope]}>{scopeLabel(candidate.scope)}</HelpLabel>
        </div>
        <div className="text-muted small">{targetingSummary(candidate)}</div>
        <HelpBadge bg={statusVariant(candidate.status)} description={ROLE_ASSET_STATUS_HELP[candidate.status]}>
          {candidate.status}
        </HelpBadge>
      </td>
      <td>
        <HelpBadge
          bg={actionVariant(candidate.recommendedAction)}
          description={ASSET_EVOLUTION_ACTION_HELP[candidate.recommendedAction]}
        >
          {candidate.recommendedActionLabel}
        </HelpBadge>
      </td>
      <td>
        <HelpBadge bg={riskVariant(candidate.riskLevel)} description={RISK_HELP[candidate.riskLevel]}>
          {candidate.riskLevel}
        </HelpBadge>
      </td>
      <td>
        <div className="fw-semibold">
          {formatCount(candidate.metrics.adoptedEmployees)} / {formatCount(candidate.metrics.eligibleEmployees)}
        </div>
        <div className="text-muted small">{formatCount(candidate.metrics.activeEmployees)} active employees</div>
      </td>
      <td>
        <div className="fw-semibold">{formatCount(candidate.metrics.invocations)}</div>
        <div className="text-muted small">{formatCount(candidate.metrics.impactedSessions)} sessions</div>
      </td>
      <td>
        <HelpBadge bg={candidate.metrics.errors > 0 ? 'danger' : 'secondary'} description={HELP_TEXT.failures}>
          {formatCount(candidate.metrics.errors)}
        </HelpBadge>
        <div className="text-muted small">
          {Math.round(candidate.metrics.errorRate * 100)}% error · {formatCount(candidate.metrics.skipped)} skipped
        </div>
      </td>
      <td>{formatCost(candidate.metrics.impactedCostUsd)}</td>
      <td style={{ minWidth: 320 }}>
        <div>{candidate.suggestedNextStep}</div>
        <div className="text-muted small mt-1">{candidate.evidenceSummary}</div>
      </td>
    </tr>
  );
}

function targetingSummary(candidate: AgentAssetEvolutionCandidate) {
  if (candidate.scope === 'company_wide') {
    return 'Company-wide';
  }
  return candidate.roles.length ? candidate.roles.map(roleLabel).join(', ') : '-';
}

function actionVariant(action: AgentAssetEvolutionAction) {
  switch (action) {
    case 'investigate_failures':
      return 'danger';
    case 'review_instructions':
      return 'warning';
    case 'improve_onboarding':
      return 'info';
  }
}

function riskVariant(risk: AgentAssetEvolutionRisk) {
  switch (risk) {
    case 'high':
      return 'danger';
    case 'medium':
      return 'warning';
    case 'low':
      return 'secondary';
  }
}

export default AgentAssetEvolutionReviewQueuePage;
